(() => {
  'use strict';

  const sectionNames = ['cart-drawer', 'cart-bubble'];
  let requestInFlight = false;

  const route = (name, fallback) => {
    const value = window.theme && window.theme.routes ? window.theme.routes[name] : '';
    return value || fallback;
  };

  const shopRoot = () => {
    const root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
    return root.endsWith('/') ? root : `${root}/`;
  };

  const drawer = () => document.getElementById('Cart-Drawer');

  const showError = (message) => {
    const error = drawer()?.querySelector('[data-wdg-cart-error]');
    if (!error) return;
    error.textContent = message || 'Something went wrong. Please try again.';
    error.hidden = false;
  };

  const clearError = () => {
    const error = drawer()?.querySelector('[data-wdg-cart-error]');
    if (!error) return;
    error.hidden = true;
    error.textContent = '';
  };

  const showDiscountMessage = (message, type = 'error') => {
    const messageElement = drawer()?.querySelector('[data-wdg-discount-message]');
    if (!messageElement) return;
    messageElement.textContent = message || '';
    messageElement.dataset.type = type;
    messageElement.hidden = !message;
  };

  const clearDiscountMessage = () => showDiscountMessage('');

  const parseSection = (html, selector) => {
    if (!html) return null;
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  };

  const renderSections = (state) => {
    if (!state || !state.sections) return;

    const incomingDrawer = parseSection(state.sections['cart-drawer'], '.cart-drawer');
    const currentDrawer = drawer();
    if (incomingDrawer && currentDrawer) {
      currentDrawer.innerHTML = incomingDrawer.innerHTML;
      const incomingStyle = incomingDrawer.getAttribute('style');
      if (incomingStyle) currentDrawer.setAttribute('style', incomingStyle);
    }

    const currentBubble = document.getElementById('cart-drawer-toggle');
    const incomingCount = parseSection(state.sections['cart-bubble'], '.thb-item-count');
    const currentCount = currentBubble?.querySelector('.thb-item-count');
    if (incomingCount && currentCount) currentCount.innerHTML = incomingCount.innerHTML;

    document.body.classList.add('open-cc', 'open-cart');
    currentDrawer?.classList.add('active');
    clearError();

    try {
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: state } }));
    } catch (_) {}
  };

  const requestCart = async (url, payload, busyElement, options = {}) => {
    const { render = true } = options;
    if (requestInFlight) return null;
    requestInFlight = true;
    clearError();
    busyElement?.classList.add('is-loading');
    if ('disabled' in (busyElement || {})) busyElement.disabled = true;

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          ...payload,
          sections: sectionNames,
          sections_url: window.location.pathname
        })
      });

      const state = await response.json();
      if (!response.ok || state.status || state.errors) {
        const message = state.description || state.message || state.errors || 'Unable to update your cart.';
        throw new Error(typeof message === 'string' ? message : 'Unable to update your cart.');
      }

      if (render) renderSections(state);
      return state;
    } catch (error) {
      showError(error.message);
      throw error;
    } finally {
      requestInFlight = false;
      busyElement?.classList.remove('is-loading');
      if ('disabled' in (busyElement || {})) busyElement.disabled = false;
    }
  };

  const getCart = async () => {
    const response = await fetch(`${shopRoot()}cart.js`, {
      headers: {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      cache: 'no-store'
    });
    if (!response.ok) throw new Error('Unable to read the current cart.');
    return response.json();
  };

  const applicableDiscountCodes = (cart) => {
    const codes = Array.isArray(cart?.discount_codes) ? cart.discount_codes : [];
    return [...new Set(
      codes
        .filter((discount) => discount && discount.code && discount.applicable !== false)
        .map((discount) => String(discount.code).trim())
        .filter(Boolean)
    )];
  };

  const codeIsApplied = (cart, requestedCode) => {
    const requested = String(requestedCode || '').trim().toLowerCase();
    if (!requested) return false;

    if (applicableDiscountCodes(cart).some((code) => code.toLowerCase() === requested)) return true;

    const cartApplications = Array.isArray(cart?.cart_level_discount_applications)
      ? cart.cart_level_discount_applications
      : [];
    if (cartApplications.some((application) => String(application?.title || '').trim().toLowerCase() === requested)) {
      return true;
    }

    const items = Array.isArray(cart?.items) ? cart.items : [];
    return items.some((item) => {
      const discounts = Array.isArray(item?.discounts) ? item.discounts : [];
      if (discounts.some((discount) => String(discount?.title || '').trim().toLowerCase() === requested)) return true;

      const allocations = Array.isArray(item?.discount_allocations) ? item.discount_allocations : [];
      return allocations.some((allocation) => {
        const application = allocation?.discount_application || allocation?.discountApplication;
        return String(application?.title || '').trim().toLowerCase() === requested;
      });
    });
  };

  const updateDiscountCodes = (codes, busyElement, options = {}) => {
    const normalizedCodes = [...new Set(
      (Array.isArray(codes) ? codes : [])
        .map((code) => String(code || '').trim())
        .filter(Boolean)
    )];

    return requestCart(
      `${shopRoot()}cart/update.js`,
      { discount: normalizedCodes.join(',') },
      busyElement,
      options
    );
  };

  const changeLine = async (line, quantity, item) => {
    if (!line && line !== 0) return;
    try {
      window.dispatchEvent(new CustomEvent('line-item:change:start', { detail: { quantity } }));
    } catch (_) {}

    try {
      let state = await requestCart(
        route('cart_change_url', `${shopRoot()}cart/change.js`),
        { line: Number(line), quantity: Number(quantity) },
        item
      );

      // A discount code can persist on Shopify's cart token after the last line is
      // removed. Explicitly clear it so it doesn't silently apply to the next cart.
      if (state && Number(state.item_count) === 0) {
        state = await updateDiscountCodes([], item);
      }

      try {
        window.dispatchEvent(new CustomEvent('line-item:change:end', { detail: { quantity, cart: state } }));
      } catch (_) {}
    } catch (_) {}
  };

  const addVariant = async (variantId, card) => {
    try {
      const state = await requestCart(
        route('cart_add_url', `${shopRoot()}cart/add.js`),
        { items: [{ id: Number(variantId), quantity: 1 }] },
        card
      );
      try {
        document.dispatchEvent(new CustomEvent('cart:item-added', { detail: { cart: state } }));
      } catch (_) {}
    } catch (_) {}
  };

  const applyDiscount = async (button) => {
    const form = button.closest('[data-wdg-discount-form]');
    const input = form?.querySelector('[data-wdg-discount-input]');
    const code = String(input?.value || '').trim();

    clearDiscountMessage();
    if (!code) {
      showDiscountMessage('Enter a discount code first.');
      input?.focus();
      return;
    }

    try {
      const before = await getCart();
      const existingCodes = applicableDiscountCodes(before);

      if (existingCodes.some((existingCode) => existingCode.toLowerCase() === code.toLowerCase())) {
        showDiscountMessage('That discount code is already applied.', 'success');
        return;
      }

      const requestedCodes = [...existingCodes, code];
      const updateState = await updateDiscountCodes(requestedCodes, button, { render: false });
      const verifiedCart = await getCart();

      if (!codeIsApplied(verifiedCart, code)) {
        // Remove the rejected code while preserving any codes that were valid
        // before this attempt, then show the error inside the refreshed drawer.
        await updateDiscountCodes(existingCodes, button);
        showDiscountMessage('That discount code is invalid or cannot be used with the items in your cart.');
        drawer()?.querySelector('[data-wdg-discount-input]')?.focus();
        return;
      }

      renderSections(updateState);
      const refreshedInput = drawer()?.querySelector('[data-wdg-discount-input]');
      if (refreshedInput) refreshedInput.value = '';
    } catch (error) {
      showDiscountMessage(error.message || 'The discount code could not be applied. Please try again.');
    }
  };

  const removeDiscount = async (button) => {
    const code = String(button.dataset.discountCode || '').trim();
    if (!code) return;

    try {
      const cart = await getCart();
      const remainingCodes = applicableDiscountCodes(cart).filter(
        (existingCode) => existingCode.toLowerCase() !== code.toLowerCase()
      );
      await updateDiscountCodes(remainingCodes, button);
    } catch (error) {
      showDiscountMessage(error.message || 'The discount code could not be removed. Please try again.');
    }
  };

  // Capture discount clicks before the old delegated Reformation handlers. The
  // redesigned drawer uses data attributes, so no legacy /discount/ redirect runs.
  document.addEventListener('click', (event) => {
    const discountApplyButton = event.target.closest('[data-wdg-discount-apply]');
    if (discountApplyButton) {
      event.preventDefault();
      event.stopImmediatePropagation();
      applyDiscount(discountApplyButton);
      return;
    }

    const discountRemoveButton = event.target.closest('[data-wdg-discount-remove]');
    if (discountRemoveButton) {
      event.preventDefault();
      event.stopImmediatePropagation();
      removeDiscount(discountRemoveButton);
      return;
    }

    const quantityButton = event.target.closest('[data-wdg-cart-quantity]');
    if (quantityButton) {
      event.preventDefault();
      const current = Number(quantityButton.dataset.current || 1);
      const step = Math.max(1, Number(quantityButton.dataset.step || 1));
      const min = Math.max(1, Number(quantityButton.dataset.min || 1));
      const max = Number(quantityButton.dataset.max || 0);
      const action = quantityButton.dataset.wdgCartQuantity;
      let next = action === 'plus' ? current + step : current - step;
      next = Math.max(min, next);
      if (max > 0) next = Math.min(max, next);
      if (next !== current) {
        changeLine(quantityButton.dataset.line, next, quantityButton.closest('[data-wdg-cart-item]'));
      }
      return;
    }

    const removeButton = event.target.closest('[data-wdg-cart-remove]');
    if (removeButton) {
      event.preventDefault();
      changeLine(removeButton.dataset.line, 0, removeButton.closest('[data-wdg-cart-item]'));
    }
  }, true);

  document.addEventListener('keydown', (event) => {
    const input = event.target.closest('[data-wdg-discount-input]');
    if (!input || event.key !== 'Enter') return;
    event.preventDefault();
    const button = input.closest('[data-wdg-discount-form]')?.querySelector('[data-wdg-discount-apply]');
    if (button) applyDiscount(button);
  });

  document.addEventListener('input', (event) => {
    if (event.target.closest('[data-wdg-discount-input]')) clearDiscountMessage();
  });

  document.addEventListener('change', (event) => {
    const checkbox = event.target.closest('[data-wdg-cart-upsell]');
    if (!checkbox) return;

    const card = checkbox.closest('[data-wdg-cart-upsell-card]');
    card?.classList.toggle('is-selected', checkbox.checked);

    if (checkbox.checked) {
      addVariant(checkbox.dataset.variantId, card);
    } else if (checkbox.dataset.line) {
      changeLine(checkbox.dataset.line, 0, card);
    }
  });
})();
